import { IDBBlockstore } from 'blockstore-idb'
import { base32upper } from 'multiformats/bases/base32'
import type { CID } from 'multiformats/cid'
import { Logger } from 'besonders-logger'

const { WARN, DEBUG } = Logger.setup(Logger.DEBUG)

let blockStore: IDBBlockstore
let openPromise: Promise<void> | null = null

export function getBlockStore() {
	if (!blockStore) blockStore = new IDBBlockstore('ipld/blocks')
	return blockStore
}

/** Ensure the shared blockstore is open. Safe to call multiple times. */
export async function ensureBlockStoreOpen() {
	if (!openPromise) {
		const store = getBlockStore()
		openPromise = store.open()
	}
	return openPromise
}

/**
 * Same key scheme as IDBBlockstore's private `#encode` (blockstore-idb, default base32upper) -
 * duplicated here because it's a real JS private field we can't call from outside the class.
 */
function encodeBlockKey(cid: CID): string {
	return `/${base32upper.encoder.encode(cid.multihash.bytes)}`
}

/**
 * Bulk-write blocks in a single IDB transaction instead of one `put()` per block.
 * `IDBBlockstore.put()` (blockstore-idb, via `idb`'s `db.put()` shorthand) opens a fresh
 * transaction per call - awaiting that in a loop for tens of thousands of blocks (e.g. a large
 * gateway pull) serializes one full transaction round-trip per block, which can take well over a
 * minute. Batching into one transaction with unawaited `tx.store.put()` calls (only the final
 * `tx.done` is awaited) reduces that to a single round-trip.
 *
 * Falls back to the sequential per-block path if the underlying db/location aren't reachable
 * (e.g. a future blockstore-idb version renames its internals) so correctness never depends on
 * this optimization.
 */
export async function putManyBlocks(pairs: Array<{ cid: CID; bytes: Uint8Array }>) {
	if (!pairs.length) return
	await ensureBlockStoreOpen()
	// `idb` (the lib blockstore-idb wraps) isn't a direct dependency, so we can't import
	// IDBPDatabase's type - use `any` for the db handle rather than pull in a transitive dep's types.
	const store = getBlockStore() as unknown as { db?: any; location?: string } // eslint-disable-line @typescript-eslint/no-explicit-any
	const db = store.db
	const location = store.location
	if (!db || !location) {
		WARN('[putManyBlocks] could not reach IDBBlockstore internals, falling back to sequential put')
		const seqStore = getBlockStore()
		for (const { cid, bytes } of pairs) await seqStore.put(cid, bytes)
		return
	}
	const tx = db.transaction(location, 'readwrite')
	for (const { cid, bytes } of pairs) {
		tx.store.put(bytes, encodeBlockKey(cid)) // not awaited - stays in the same transaction
	}
	await tx.done
	DEBUG('[putManyBlocks] wrote', pairs.length, 'blocks in one transaction')
}

/**
 * `BlockStoreForPull`-shaped wrapper (see @wovin/connect-ucan-store-proxy) around the
 * shared blockstore, adding `putMany` so `carMirrorPull` can batch its writes the same
 * way `putManyBlocks` already batches the gateway-fetch write path — same underlying
 * IDB-transaction-per-call cost, same fix. Use this (not raw `getBlockStore()`)
 * wherever a `BlockStoreForPull` is passed to `pullDag`/`carMirrorPull`.
 */
export function getBlockStoreForPull() {
	const store = getBlockStore()
	return {
		has: (key: CID) => store.has(key),
		get: (key: CID) => store.get(key),
		put: (key: CID, val: Uint8Array) => store.put(key, val),
		putMany: putManyBlocks,
	}
}
